/* eslint-disable react/prop-types */
import * as popUpStyles from '../styles/layout/search-pop-up.module.scss';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { concat, map } from 'lodash';
import { getBodyNews, getHeaderNews } from '../utils/guides';
import EventGuideLayout from '../components/layout/templates/EventGuideLayout';
import FocusCarrousel from '../components/shared/carrousels/FocusCarrousel';
import HeaderCarrousel from '../components/shared/carrousels/HeaderCarrousel';
import InfinitePagination from '../components/shared/InfinitePagination';
import Pagination from '../components/shared/Pagination';
import Results from '../components/actualites/Results';
import SearchCriterias from '../components/actualites/SearchCriterias';
import Seo from '../seo/seo';
import algoliasearch from 'algoliasearch';
import { getSEOInformations } from '../utils/parsing';
import { graphql, Slice } from 'gatsby';
import { useTranslation, getI18n } from 'react-i18next';
import dompurify from 'isomorphic-dompurify';
import {dompurifyConfig, setupi18n} from '../utils/utils';
import '../services/i18n';

export const Head = ({ data, pageContext, location }) => {

  const {locale, generalSeo, websiteSeo, name } = pageContext;
  const {
    canonical_link,
    pageSeo,
    metaDescription,
  } = getSEOInformations(data);

  return (
    <Seo
      canonicalLink={canonical_link}
      generalSeo={generalSeo}
      language={locale}
      location={location}
      metaDescription={metaDescription}
      pageSeo={pageSeo}
      type={pageContext.pageType}
      website={name}
      websiteSeo={websiteSeo}
    />
  );
};

export default function NewsGuide ({ data, pageContext }) {
  const {
    locale,
    websiteRoot,
    breadcrumbs,
    pagination,
    guideHash: searchGuideID,
    categories: fltCategories,
  } = pageContext;
  const {t} = useTranslation();
  setupi18n(locale, getI18n());
  const guide = data.cmsPortalNewsGuide;
  const guideBodyNews = React.useMemo(() => {
    return data.news.nodes || [];
  }, [data.news.nodes]);

  const {
    content,
    news_guide_focus: focusNews,
    news_per_page: newsPerPage,
    headerNews: guideHeaderNews,
  } = guide;

  const display = guide?.display || 'GRID';

  const client = useMemo(() => (
    algoliasearch(process.env.GATSBY_ALGOLIA_APP_ID, process.env.GATSBY_ALGOLIA_SEARCH_KEY)
  ), []);
  const index = useMemo(() => (
    client.initIndex(`${process.env.GATSBY_ALGOLIA_INDEX_NAME}_publishedAt_timestamp_desc`)
  ), [client]);
  const sanitizer = dompurify.sanitize;

  // first state
  const headerNews = React.useMemo(
    () => getHeaderNews(guideHeaderNews, locale, websiteRoot),
    [guideHeaderNews, locale, websiteRoot]
  );
  const bodyNews = React.useMemo(() => {
    return getBodyNews(guideBodyNews, locale, websiteRoot);
  }, [guideBodyNews, locale, websiteRoot]);

  // current page news
  const [websiteNews, setWebsiteNews] = useState(bodyNews);
  // categories
  const [categories, setCategories] = useState(fltCategories || []);

  //store Search Engine Filter
  const [filter, setFilter] = useState([]);

  const getSelectedCategories = useCallback((selectedCategories) => {
    setFilter(selectedCategories);
  }, []);

  const getSelectedCategoriesFromNews = useCallback(
    (selectedCategories) => {
      setFilter(selectedCategories);
      const newCategories = map(categories, (category) => {
        category.clicked = category.label === selectedCategories[0].label;
        return category;
      });
      setCategories(newCategories);
    },
    [categories]
  );

  /**
   * Guide filter
   */
  const filterToAlgoliaQuery = useCallback(() => {
    const qpts = [`type:"news-guide-news" AND guideID:"${searchGuideID}"`];
    const subcategories = [];

    filter.forEach((el) => {
      if (el.type === 'category') {
        qpts.push(` categories.label:"${el.label}" `);
      } else {
        subcategories.push(` subcategories.label:"${el.label}" `);
      }
    });

    if (subcategories.length > 0) {
      qpts.push(' (' + subcategories.join(' OR ') + ') ');
    }
    return qpts.join(' AND ');
  }, [filter, searchGuideID]);

  const makeUri = useCallback(
    (refObj) => {
      return `${websiteRoot}/${refObj}`.replace(/\/\/+/g, '/');
    },
    [websiteRoot]
  );

  const search = useCallback(
    (page, prev) => {
      index
        .search('*', {
          page: page,
          filters: filterToAlgoliaQuery(),
          hitsPerPage: newsPerPage,
        })
        .then(({ hits, nbHits, page, nbPages }) => {
          hits = concat(prev, hits);
          setWebsiteNews({
            hits: hits.map((el) => {
              return {
                ...el,
                slug: makeUri(el.refObjectPath),
              };
            }),
            nbHits: nbHits,
            nbPages: nbPages,
            page: page,
          });
        })
        .catch((err) => {
          console.error(err);
          return [];
        });
    },
    [filterToAlgoliaQuery, index, makeUri, newsPerPage]
  );

  useEffect(() => {
    if (filter.length > 0) {
      search(0, []);
    }
  }, [filter, search]);

  /**
   * Filter renderer
   */
  const filterRenderer = () => {
    if (filter.length > 0) {
      return (
        `${websiteNews?.nbHits} ${
          websiteNews?.nbHits === 1
            ? t('news.guide.page.result')
            : t('news.guide.page.results')
        }` + [' « ', filter.map((e) => e.label).join(', '), ' » '].join(' ')
      );
    }
    return null;
  };

  let resultElements = null;

  const setFocus = () => {
    if (resultElements) {
      resultElements[resultElements.length - 1].focus();
    }
  };

  if (typeof window !== 'undefined') {
    resultElements = document.getElementsByName('news-focus');
  }
  return (
    <Slice alias={'internal-page-layout'}>
      <EventGuideLayout
        filAriane={breadcrumbs}
        header={
          headerNews?.length > 0 ? (
            <HeaderCarrousel
              carrousel
              color="RED"
              items={headerNews}
              locale={locale}
              news
              siteURL={websiteRoot}
            />
          ) : null
        }
        title={guide?.title || t('news.guide.page.title')}
      >
        <div className="newsGuideStyles.newsguideContainer">
          {content && (
            <div
              className="ckContent"
              dangerouslySetInnerHTML={{
                __html: sanitizer(content, dompurifyConfig),
              }}
            >
            </div>
          )}
          {focusNews?.length > 0 && focusNews[0].carousel_item?.length > 0 && (
            <>
              <div style={{ marginTop: 40 }}>
                {focusNews.title && <p>{focusNews[0].title}</p>}
              </div>
              <div style={{ marginTop: 20 }}>
                <FocusCarrousel items={focusNews[0].carousel_item} />
              </div>
            </>
          )}
          {categories && (
            <>
              <div style={{ marginTop: 40 }}>
                <h2
                  style={{
                    fontSize: 25,
                    fontWeight: 900,
                    textTransform: 'uppercase'
                  }}
                >{t('news.guide.page.title')}
                </h2>
              </div>
              <div style={{ marginBottom: 20, marginTop: 20 }}>
                <SearchCriterias
                  categories={categories}
                  getSelectedCategories={getSelectedCategories}
                />
              </div>
            </>
          )}
          {filter?.length > 0 && websiteNews?.nbHits > -1 && (
            <>
              <div style={{ marginBottom: 20 }}>
                <h3>{filterRenderer()}</h3>
              </div>
              <Results
                display={display}
                getCategories={getSelectedCategoriesFromNews}
                locale={locale}
                news={websiteNews.hits}
              />
            </>
          )}
          {filter?.length < 1 &&
            bodyNews?.length > 0 && (
            <Results
              display={display}
              getCategories={getSelectedCategoriesFromNews}
              locale={locale}
              news={bodyNews}
            />
          )}
        </div>
        {filter?.length < 1 && pagination?.totalPages > 1 && (
          <div style={{ marginTop: 50, marginBottom: 50 }}>
            <Pagination pagination={pagination}></Pagination>
          </div>
        )}
        {filter?.length > 0 && websiteNews?.page + 1 < websiteNews?.nbPages && (
          <div className={popUpStyles.toutVoir}>
            <InfinitePagination
              label={t('nav.seeMore')}
              onclick={search}
              pageIndex={websiteNews.page}
              prevItems={websiteNews.hits}
              setFocus={setFocus}
            />
          </div>
        )}
      </EventGuideLayout>
    </Slice>
  );
}

export const query = graphql`
  query ($guideId: String!, $websiteId: String!, $bodyElementIds: [String!]) {
    cmsPortalNewsGuide(
      id: { eq: $guideId }
      websites: { elemMatch: { id: { eq: $websiteId } } }
    ) {
      title
      id
      slug
      canonical_link
      locale
      content
      display
      news_filter {
        categories {
          subcategories {
            label
          }
          label
        }
      }
      news_per_page
      header {
        promoted_news {
          ...NewsData
        }
        number_of_news
      }
      headerNews {
        ...NewsData
      }
      news_guide_focus {
        ...widgetCarousel
      }
    }
    news: allCmsPortalNewsItem(
      filter: { id: { in: $bodyElementIds } }
      sort: { publishedAt : DESC }
    ) {
      nodes {
        ...NewsData
      }
    }
  }
`;
